import React, { useState, useEffect, useMemo, useRef } from 'react';
import Spline from '@splinetool/react-spline';
import { useNavigate } from 'react-router-dom';
import { Container } from "react-bootstrap";
import 'animate.css';
import TrackVisibility from 'react-on-screen';
import './../App.css';

export default function SplineEvents({ setIsSkillsOpen, language, languageSelected }) {
    const navigate = useNavigate();
    const [showGreeting, setShowGreeting] = useState(false);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(true);
    const [splineInternalLoaded, setSplineInternalLoaded] = useState(false); // Tracks Spline's internal onLoad/onProgress=1
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [showInstructions, setShowInstructions] = useState(false);
    const [virtualProgressComplete, setVirtualProgressComplete] = useState(false); // New state for virtual progress
    const [isMobile, setIsMobile] = useState(false);
    const [interactionTimeout, setInteractionTimeout] = useState(null);

    const splineRef = useRef(null);

    // Effect to manage body overflow and start virtual loading progress
    useEffect(() => {
        document.body.style.overflow = 'hidden'; // Keep body overflow hidden initially

        // Virtual loading progress
        const virtualLoadingInterval = setInterval(() => {
            setLoadingProgress(prev => {
                if (prev >= 95) { // Cap virtual progress, let Spline's actual load handle the last bit
                    clearInterval(virtualLoadingInterval);
                    setVirtualProgressComplete(true); // Mark virtual progress as complete
                    return 95;
                }
                return prev + 1;
            });
        }, 40);

        // Cleanup: Clear interval and restore overflow on unmount
        return () => {
            clearInterval(virtualLoadingInterval);
            document.body.style.overflow = 'auto';
        };
    }, []); // Runs only once on mount

    // Effect to handle the actual hiding of the loading screen
    useEffect(() => {
        // Condition to hide loading screen:
        // Either Spline has reported loaded AND virtual progress is high/complete
        // OR a fallback timeout occurs (if Spline events are unreliable)
        if (splineInternalLoaded && loadingProgress >= 95) {
            console.log('Spline and virtual progress complete. Hiding loading screen in 500ms.');
            const timeoutId = setTimeout(() => {
                setLoading(false);
                setShowInstructions(true); // Show instructions after loading
                document.body.style.overflow = 'auto'; // Restore scroll
                console.log('Loading screen hidden, overflow restored.');
            }, 500); // Small delay for smooth transition
            return () => clearTimeout(timeoutId);
        } else if (virtualProgressComplete && !splineInternalLoaded) {
            // Fallback: If virtual progress hit 95% and Spline hasn't loaded after some time
            // This is a safety net if Spline's events don't fire consistently
            console.log('Virtual progress complete, but Spline not fully loaded. Initiating fallback timeout.');
            const fallbackTimeout = setTimeout(() => {
                console.log('Fallback timeout triggered. Hiding loading screen.');
                setLoading(false);
                document.body.style.overflow = 'auto';
            }, 3000); // Give Spline an additional 3 seconds after virtual progress completes
            return () => clearTimeout(fallbackTimeout);
        }
    }, [splineInternalLoaded, loadingProgress, virtualProgressComplete]); // Depend on these states

    // Detect mobile device
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    // Cleanup interaction timeout on unmount
    useEffect(() => {
        return () => {
            if (interactionTimeout) {
                clearTimeout(interactionTimeout);
            }
        };
    }, [interactionTimeout]);


    const handleInteraction = (e) => {
        const eventType = e.type;
        const targetName = e.target?.name || 'unknown';

        if (eventType === 'mouseDown' || eventType === 'click') {
            if (['laptop', 'mac'].includes(targetName.toLowerCase())) {
                console.log(`Clicked ${targetName}. Navigating to /home.`);
                navigate('/home');
            }
        }

        // Handle instructions visibility based on device type
        if (isMobile) {
            // On mobile: hide instructions during interaction
            if (showInstructions) {
                setShowInstructions(false);
            }
        }
        // On desktop: instructions remain visible (no change needed)

        // Clear existing timeout
        if (interactionTimeout) {
            clearTimeout(interactionTimeout);
        }

        // Set timeout to show instructions after inactivity
        // Mobile: 3 seconds, Desktop: 10 seconds (but only if not already visible)
        const timeoutDelay = isMobile ? 3000 : 10000;
        const timeout = setTimeout(() => {
            if (!showInstructions) {
                setShowInstructions(true);
            }
        }, timeoutDelay);

        setInteractionTimeout(timeout);
    };

    // Callback for Spline's onProgress event (reports 0 to 1)
    const handleSplineProgress = (progress) => {
        const actualProgress = Math.floor(progress * 100);
        // console.log(`Spline Progress: ${actualProgress}%`); // Uncomment for detailed progress logging
        setLoadingProgress(prev => Math.max(prev, actualProgress)); // Always take the higher of virtual or actual
        if (progress === 1) {
            console.log('Spline reported 100% progress via onProgress.');
            setSplineInternalLoaded(true);
        }
    };

    // Callback for Spline's onLoad event (when the model is ready)
    const handleSplineLoad = (spline) => {
        console.log('Spline model loaded (onLoad event triggered).');
        setSplineInternalLoaded(true); // Explicitly mark Spline as loaded
        // Ensure progress is 100% on actual load
        setLoadingProgress(100);

        try {
            if (spline.getObjectByName) {
                ['macBook', 'laptop', 'mac'].forEach(name => {
                    const obj = spline.getObjectByName(name);
                    if (obj) console.log(`Found object: ${name}`, obj);
                });
            }
        } catch (err) {
            console.error('Error finding objects in Spline:', err);
        }
    };

    const languages = useMemo(() => [
        { lang: 'Spanish', greeting: '¡Hola!' },
        { lang: 'English', greeting: 'Hello!' },
        { lang: 'Thai', greeting: 'สวัสดีครับ/ค่ะ!' },
        { lang: 'German', greeting: 'Guten Tag!' },
        { lang: 'Italian', greeting: 'Ciao!' },
        { lang: 'Portuguese', greeting: 'Olá!' },
        { lang: 'Japanese', greeting: 'こんにちは！' },
        { lang: 'Chinese', greeting: '你好！' },
        { lang: 'Korean', greeting: '안녕하세요!' },
        { lang: 'Arabic', greeting: 'مرحباً!' },
    ], []);

    useEffect(() => {
        let index = 0;
        let greetingInterval;

        const showNextGreeting = () => {
            if (index < languages.length) {
                setText(languages[index].greeting);
                setShowGreeting(true);
                index++;
                greetingInterval = setTimeout(showNextGreeting, 2000); // Increased from 1000 to 2000ms
            } else {
                // All greetings shown — remove the last one after 2 seconds
                setTimeout(() => {
                    setShowGreeting(false);
                    setText("");
                }, 2000); // Increased from 1000 to 2000ms
            }
        };

        // Only start greeting animation after loading is complete AND language has been selected
        if (!loading && languageSelected) {
            console.log('Loading complete and language selected, starting greeting animation.');
            showNextGreeting();
        }

        return () => clearTimeout(greetingInterval); // Use clearTimeout here
    }, [languages, loading, languageSelected]); // Depend on 'loading' and 'languageSelected' states

    const LoadingScreen = () => (
        <div className="loading-screen">
            <div className="cyber-loader"></div>
            <h1 className="cyber-title">{language === 'es' ? 'INICIALIZANDO PORTAFOLIO 3D INTERACTIVO' : 'INITIALIZING INTERACTIVE 3D PORTFOLIO'}</h1>
            <div className="progress-container">
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${loadingProgress}%` }}>
                        <div className="progress-glow"></div>
                    </div>
                </div>
                <div className="progress-text">{loadingProgress}%</div>
            </div>
            {/* Optional: Add a subtle loading message or animation */}
            {!splineInternalLoaded && loadingProgress < 100 && (
                <div className="loading-message">
                    {language === 'es' ? 'Cargando activos 3D...' : 'Loading 3D assets...'}
                </div>
            )}
        </div>
    );

    const InstructionsOverlay = () => (
        <div className={`cyber-overlay ${showInstructions && !loading ? 'visible' : ''}`}> {/* Restored showInstructions condition */}
            <div className="overlay-header">
                <div className="info-icon">ℹ️</div>
                <h3>{language === 'es' ? 'CONTROLES' : 'CONTROLS'}</h3>
            </div>
            <ul className="instruction-list">
                <li><span>⟳ {language === 'es' ? 'ROTAR' : 'ROTATE'}</span> - {isMobile ? (language === 'es' ? '1 Dedo + Arrastrar' : '1 Finger + Drag') : (language === 'es' ? 'Click + Arrastrar' : 'Click + Drag')}</li>
                <li><span>⇆ {language === 'es' ? 'PANORÁMICA' : 'PAN'}</span> - {isMobile ? (language === 'es' ? '2 Dedos + Arrastrar' : '2 Fingers + Drag') : (language === 'es' ? 'Click Derecho + Arrastrar' : 'Right Click + Drag')}</li>
                <li><span>🔍 {language === 'es' ? 'ZOOM' : 'ZOOM'}</span> - {isMobile ? (language === 'es' ? 'Pellizcar (2 Dedos)' : 'Pinch (2 Fingers)') : (language === 'es' ? 'Rueda del ratón' : 'Scroll Wheel')}</li>
                <li><span>💻 {language === 'es' ? 'Currículum Vitae' : 'Curriculum Vitae'}</span> - {language === 'es' ? 'Tocar MacBook' : 'Tap MacBook'}</li>
                <li><span>🤖 {language === 'es' ? 'INTERACTUAR CON ChatCJR' : 'INTERACT WITH ChatCJR'}</span> - {language === 'es' ? 'Tocar Robot' : 'Tap Robot'}</li>
            </ul>
        </div>
    );

    return (
        <div className="spline-container" style={{ height: "100vh", width: "100%", position: "relative" }}>
            {/* Cyber Background */}
            <div className="cyber-background">
                <div className="cyber-grid"></div>
                <div className="floating-particles">
                    {[...Array(40)].map((_, i) => (
                        <div key={i} className={`particle particle-${i % 4 + 1}`}></div>
                    ))}
                </div>
            </div>

            {loading && <LoadingScreen />}

            {/* Render the Spline component always, but control its visibility with CSS */}
            <Spline
                scene={process.env.REACT_APP_SPLINE_XYZ}
                onLoad={handleSplineLoad}
                onProgress={handleSplineProgress}
                onMouseDown={handleInteraction}
                onMouseUp={handleInteraction}
                onClick={handleInteraction}
                onHover={handleInteraction}
                onTouchStart={handleInteraction}
                ref={splineRef}
                // Apply a class that hides the spline until loading is false
                className={`spline-model ${loading ? 'spline-hidden' : 'spline-visible'}`}
            />

            <Container style={{ position: "relative" }}>
                <div className="greeting-container">
                    <TrackVisibility>
                        {({ isVisible }) => (
                            <div className={isVisible ? "animate__animated animate__fadeIn" : ""}>
                                <h1 className="palace-script">
                                    {showGreeting && text}
                                </h1>
                            </div>
                        )}
                    </TrackVisibility>
                </div>
            </Container>

            {/* Render instructions only after loading is false and showInstructions is true */}
            {!loading && <InstructionsOverlay />}
        </div>
    );
}